import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Medal, Loader2, Lock, Check, Pencil, X } from "lucide-react";
import SeatGrid from "@/components/founding/SeatGrid";
import { FoundingShareButton } from "@/components/founding/FoundingShareCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const pad3 = (n) => String(n).padStart(3, "0");

// Member seat picker (public seats 11-100). The lowest available is the default suggestion with a
// one-tap accept, and the grid — a co-equal choice — lets a member pick any open number. A
// permanent number takes one deliberate confirmation. A member who took an auto-assigned number can
// CHANGE it to any unclaimed seat until it CONFIRMS at ID verification, after which it's permanent.
// If a seat is taken between render and submit we refresh the grid and keep the member right here.
export default function FoundingSeatPicker() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);   // seat number awaiting confirmation
  const [changing, setChanging] = useState(false); // self-change mode (grid shown for an existing holder)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/founding/seats");
      setData(data);
      setSelected((prev) => (prev && data.seats.find((s) => s.seat_number === prev && s.status === "available")) ? prev : data.lowest_available);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doClaim = async (seatNumber) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/founding/claim", { seat_number: seatNumber });
      toast.success(`Founding #${pad3(res.seat_number)} is yours. Welcome aboard.`);
      setConfirm(null);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setConfirm(null);
      if (err.response?.status === 409 && detail?.error === "seat_taken") {
        toast.error(detail.message || "That seat just went — pick another.");
        await load();
      } else { toast.error(formatError(detail)); }
    } finally { setBusy(false); }
  };

  const doChange = async (seatNumber) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/founding/change", { to_seat: seatNumber });
      toast.success(`Your founding number is now #${pad3(res.to_seat)}.`);
      setConfirm(null); setChanging(false);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setConfirm(null);
      if (err.response?.status === 409 && detail?.error === "seat_taken") {
        toast.error(detail.message || "That seat just went — pick another.");
        await load();  // refresh grid, stay in change mode
      } else { toast.error(formatError(detail)); setChanging(false); }
    } finally { setBusy(false); }
  };

  const commit = () => { if (changing) doChange(confirm); else doClaim(confirm); };

  if (!data) {
    return <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm py-8" data-testid="founding-picker-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading seats…</div>;
  }

  // Already holds a seat — the GOLD founding card. Offers CHANGE while the seat is still unconfirmed.
  if (data.my_seat && !changing) {
    const seat = pad3(data.my_seat.seat_number);
    const total = data.total_seats;
    return (
      <>
        <div data-testid="founding-picker-held"
          style={{ background: "#14110A", border: "1px solid #55451C", padding: "26px 24px" }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9.5px", letterSpacing: "0.2em", textTransform: "uppercase", color: "#C8A64E" }}>
            Founding Member
          </div>
          <div data-testid="founding-seat-hero"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: "74px", lineHeight: 0.88, margin: "6px 0 2px", color: "#FFD866" }}>
            {seat}
          </div>
          {typeof total === "number" && (
            <div data-testid="founding-seat-subline" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.16em", color: "#8A7434" }}>
              Seat {seat} of {total}
            </div>
          )}
          {data.can_change ? (
            <div className="mt-4">
              <button data-testid="founding-change-btn" onClick={() => { setChanging(true); setSelected(data.lowest_available); }}
                className="inline-flex items-center gap-2 border border-[#b08d2c] text-[#E8C46A] hover:bg-[#b08d2c] hover:text-white font-mono uppercase text-[11px] tracking-wide px-3 py-2 transition-colors">
                <Pencil className="w-3.5 h-3.5" /> Change my number
              </button>
              <p data-testid="founding-change-note" className="mt-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                You can change your number until it's confirmed at ID verification.
              </p>
            </div>
          ) : (
            <div data-testid="founding-seat-pool" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.16em", color: "#C8A64E", marginTop: "10px" }}>
              Seat locked in — confirmed and permanent
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-[#55451C]/60">
            <p className="font-mono text-[10px] uppercase tracking-wide text-[#8A7434] mb-2">Tell your people</p>
            <FoundingShareButton seat={data.my_seat.seat_number} />
          </div>
        </div>
        <ConfirmDialog changing={changing} seat={confirm} busy={busy} onCancel={() => setConfirm(null)} onConfirm={commit} />
      </>
    );
  }

  // Sold out — say so plainly; never show a claim button that can only fail.
  if (!data.my_seat && data.public_remaining <= 0) {
    return (
      <div data-testid="founding-picker-soldout" className="border border-sniper bg-surface p-6 text-center">
        <Medal className="w-7 h-7 text-muted-foreground mx-auto" />
        <p className="mt-3 font-heading text-lg uppercase tracking-tight text-foreground">All founding seats are claimed</p>
        <p className="mt-1 font-body text-sm text-secondary-foreground">
          Every one of the {data.public_seats} public founding seats has been taken. Thank you to everyone who claimed one.
        </p>
      </div>
    );
  }

  if (!data.my_seat && !data.eligible) {
    return (
      <div data-testid="founding-picker-ineligible" className="border border-sniper bg-surface p-6 text-center">
        <Lock className="w-7 h-7 text-muted-foreground mx-auto" />
        <p className="mt-3 font-body text-sm text-secondary-foreground">
          {data.ineligible_reason || "Your account isn't eligible for a founding seat right now."}
        </p>
      </div>
    );
  }

  // Claim grid (new claim) OR self-change grid (existing holder picking a new number).
  return (
    <div data-testid="founding-seat-picker">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          {changing ? "Pick a new unclaimed seat" : `${data.public_remaining} of ${data.public_seats} open`}
        </p>
        {changing ? (
          <button data-testid="founding-change-cancel" disabled={busy} onClick={() => { setChanging(false); load(); }}
            className="inline-flex items-center gap-1 font-mono uppercase text-[11px] tracking-wide text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors">
            <X className="w-3.5 h-3.5" /> Keep my current number
          </button>
        ) : (
          <button data-testid="founding-autopick" disabled={busy} onClick={() => setConfirm(data.lowest_available)}
            className="font-mono uppercase text-[11px] tracking-wide text-olive-bright hover:text-volt disabled:opacity-50 transition-colors">
            Accept lowest (#{data.lowest_available ? pad3(data.lowest_available) : "—"}) →
          </button>
        )}
      </div>

      <SeatGrid seats={data.seats} selected={selected} onSelect={setSelected} busy={busy} />

      <button
        data-testid={changing ? "founding-change-submit" : "founding-claim-btn"}
        disabled={busy || !selected}
        onClick={() => setConfirm(selected)}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#b08d2c] hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-all"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>
          : changing ? <>Change to Founding #{selected ? pad3(selected) : "—"} <Medal className="w-4 h-4" /></>
            : <>Claim Founding #{selected ? pad3(selected) : "—"} <Medal className="w-4 h-4" /></>}
      </button>

      <ConfirmDialog changing={changing} seat={confirm} busy={busy} onCancel={() => setConfirm(null)} onConfirm={commit} />
    </div>
  );
}

function ConfirmDialog({ changing, seat, busy, onCancel, onConfirm }) {
  const open = seat != null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-sm" data-testid="founding-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <Medal className="w-5 h-5 text-[#E8C46A]" />
            {changing ? "Change your founding number?" : `Claim founding seat #${seat != null ? pad3(seat) : ""}?`}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm font-body text-secondary-foreground">
          {changing
            ? <>You'll move to founding seat <span className="text-[#E8C46A] font-mono">#{seat != null ? pad3(seat) : ""}</span>. Your number becomes permanent once it's confirmed at ID verification.</>
            : <>Founding seat <span className="text-[#E8C46A] font-mono">#{seat != null ? pad3(seat) : ""}</span> will be yours. Your number is permanent — you can change it only until it's confirmed at ID verification.</>}
        </p>
        <div className="flex gap-2 pt-1">
          <button data-testid="founding-confirm-yes" disabled={busy} onClick={onConfirm}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#b08d2c] hover:brightness-110 disabled:opacity-50 text-white font-heading font-bold uppercase text-sm py-2.5 transition-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {changing ? "Confirm change" : `Claim #${seat != null ? pad3(seat) : ""}`}
          </button>
          <button data-testid="founding-confirm-cancel" disabled={busy} onClick={onCancel}
            className="border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-4 transition-colors">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
